import {User, UserResponse} from "@/app/auth/auth";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "../atoms/Avatar";

export type ChatClientSummaryProps = {
  user?: UserResponse;
  trainingProgress: any;
  nutritionProgress: any;
  isLoading?: boolean;
  onShowPreview?: () => void;
};

export const ChatClientSummary: FC<ChatClientSummaryProps> = ({
  user,
  trainingProgress,
  nutritionProgress,
  isLoading,
  onShowPreview,
}) => {
  const [t] = useTranslation();

  return (
    <div className="bg-white w-full pr-2">
      <h2 className="text-lg font-semibold text-gray-700 border-b pb-2 mb-4">
        {t("chat.app.information")}
      </h2>
      {!isLoading && !user ? (
        <div className="flex flex-1 justify-center items-center">
          {t("chat.app.no_information_to_show")}
        </div>
      ) : (
        <div className="mt-6 mx-2">
          <div className="space-y-12">
            <div className="">
              <div className="flex items-center h-full text-center">
                <div>
                  <h2 className="text-base text-[1.2rem] font-semibold leading-7 text-gray-900 mb-2">
                    {t("chat.app.general")}
                  </h2>
                </div>
              </div>
              <div className="">
                <div className="col-span-full">
                  <div>
                    {isLoading ? (
                      <div className="h-10 w-10 bg-gray-300 rounded-md animate-pulse" />
                    ) : (
                      <Avatar size="medium" src={user?.user?.avatar ?? "-"} />
                    )}
                  </div>
                </div>
                <div className={'bg-gray-100 p-3 mt-3'}>
                  <div className="col-span-full">
                    <div className={'flex'}>
                      <div className="flex justify-between mr-2">
                        <label
                            htmlFor=""
                            className="block text-sm font-medium leading-6 text-gray-600 w-[80px]"
                        >
                          {t("chat.app.name")}:
                        </label>
                      </div>
                      <div className="flex items-center">
                        {isLoading ? (
                            <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                        ) : (
                            <div className="truncate">{user?.user?.name ?? "-"}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-full">
                    <div className={'flex'}>
                      <div className="flex justify-between mr-2">
                        <label
                            htmlFor=""
                            className="block text-sm font-medium leading-6 text-gray-600 w-[80px]"
                        >
                          {t("chat.app.email")}:
                        </label>
                      </div>
                      {isLoading ? (
                          <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                      ) : (
                          <div className="truncate">{user?.user?.email ?? "-"}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-span-full">
                    <div className={'flex'}>
                      <div className="flex justify-between mr-2">
                        <label
                            htmlFor=""
                            className="block text-sm font-medium leading-6 text-gray-600 w-[80px]"
                        >
                          {t("chat.app.id")}:
                        </label>
                      </div>
                      {isLoading ? (
                          <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                      ) : (
                          <div className="truncate">{user?.user?.id ?? "-"}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-span-full">
                    <div className={'flex'}>
                      <div className="flex justify-between mr-2">
                        <label
                            htmlFor=""
                            className="block text-sm font-medium leading-6 text-gray-600 w-[80px]"
                        >
                          {t("chat.app.joined_at")}:
                        </label>
                      </div>
                      {isLoading ? (
                          <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                      ) : (
                          <div className="truncate">
                            {user?.user?.createdAt
                                ? new Date(user.user.createdAt).toLocaleString('es-CO', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: false
                                }).replace(/\//g, '-')
                                : "-"}
                          </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="!mt-5">
              <div className="">
                <div>
                  <h2 className="text-base text-[1.2rem] font-semibold leading-7 text-gray-900 mb-2">
                    {t("chat.app.training_plan")}
                  </h2>
                </div>
              </div>
              <div className="bg-gray-100 p-3">
                <div className="col-span-full">
                  <div className={'flex'}>
                    <div className="flex justify-between  mr-2">
                      <label
                        htmlFor=""
                        className="block text-sm font-medium leading-6 text-gray-600 w-[115px]"
                      >
                        {t("chat.app.plan_name")}:
                      </label>
                    </div>
                    {isLoading ? (
                      <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                    ) : (
                      <div className="truncate">
                        {trainingProgress?.planTableName ?? "-"}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-full">
                  <div className="flex">
                    <div className="flex justify-between mr-2">
                      <label
                        htmlFor=""
                        className="block text-sm font-medium leading-6 text-gray-600 w-[115px]"
                      >
                        {t("chat.app.started_at")}:
                      </label>
                    </div>
                    {isLoading ? (
                      <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                    ) : (
                      <div className="truncate">
                        {trainingProgress?.dateIni
                            ? new Date(trainingProgress.dateIni).toLocaleString('es-CO', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            }).replace(/\//g, '-')
                            : "-"
                        }
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-full">
                  <div className={'flex'}>
                    <div className="flex justify-between mr-2">
                      <label
                        htmlFor=""
                        className="block text-sm font-medium leading-6 text-gray-600 w-[115px]"
                      >
                        {t("chat.app.finishes_at")}:
                      </label>
                    </div>
                    {isLoading ? (
                      <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                    ) : (
                      <div className="truncate">
                        {trainingProgress?.dateEnd
                            ? new Date(trainingProgress.dateEnd).toLocaleString('es-CO', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            }).replace(/\//g, '-')
                            : "-"
                        }
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-full">
                  <div className={'flex'}>
                    <div className="flex justify-between mr-2">
                      <label
                        htmlFor=""
                        className="block text-sm font-medium leading-6 text-gray-600 w-[115px]"
                      >
                        {t("share.views.compliance")}:
                      </label>
                    </div>
                    {isLoading ? (
                      <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                    ) : (
                      <div className="truncate">
                        {trainingProgress?.degreeOfCompliance ?? 0}%
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-full">
                  <div className={'flex'}>
                    <div className="flex justify-between mr-2">
                      <label
                        htmlFor=""
                        className="block text-sm font-medium leading-6 text-gray-600 w-[115px]"
                      >
                        {t("share.views.assigned_by")}:
                      </label>
                    </div>
                    {isLoading ? (
                      <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                    ) : (
                      <div className="truncate">
                        {trainingProgress?.assignedBy ?? "-"}
                      </div>
                    )}
                  </div>
                </div>
                {trainingProgress && (
                    <>
                      <div className="col-span-full">
                        <div className={'flex'}>
                          <div className="flex justify-between mr-2">
                            <label
                                htmlFor=""
                                className="block text-sm font-medium leading-6 text-gray-600 w-[115px]"
                            >
                              {t("share.views.show_plan")}:
                            </label>
                          </div>
                          {(isLoading) ? (
                              <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                          ) : (
                              <div className="text-blue-700 truncate cursor-pointer" onClick={() => onShowPreview?.()}>
                                {t("share.views.show")}
                              </div>
                          )}
                        </div>
                      </div>
                    </>
                )}
              </div>
            </div>
            <div className="!mt-5">
              <div className="">
                <div>
                  <h2 className="text-base text-[1.2rem] font-semibold leading-7 text-gray-900 mb-2">
                    {t("chat.app.nutrition_plan")}
                  </h2>
                </div>
              </div>
              <div className="bg-gray-100 p-3">
                <div className="col-span-full">
                  <div className={'flex'}>
                    <div className="flex justify-between mr-2">
                      <label
                        htmlFor=""
                        className="block text-sm font-medium leading-6 text-gray-600 w-[115px]"
                      >
                        {t("chat.app.plan_name")}:
                      </label>
                    </div>
                    {isLoading ? (
                      <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                    ) : (
                      <div className="truncate">
                        {nutritionProgress?.tableNutrition ?? "-"}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-full">
                  <div className={'flex'}>
                    <div className="flex justify-between mr-2">
                      <label
                        htmlFor=""
                        className="block text-sm font-medium leading-6 text-gray-600 w-[115px]"
                      >
                        {t("chat.app.started_at")}:
                      </label>
                    </div>
                    {isLoading ? (
                      <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                    ) : (
                      <div className="truncate">
                        {nutritionProgress?.dateIni
                            ? new Date(nutritionProgress.dateIni).toLocaleString('es-CO', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            }).replace(/\//g, '-')
                            : "-"

                        }
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-full">
                  <div className={'flex'}>
                    <div className="flex justify-between mr-2">
                      <label
                        htmlFor=""
                        className="block text-sm font-medium leading-6 text-gray-600 w-[115px]"
                      >
                        {t("share.views.compliance")}:
                      </label>
                    </div>
                    {isLoading ? (
                      <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                    ) : (
                      <div className="truncate">
                        {nutritionProgress?.dateEnd
                            ? new Date(nutritionProgress.dateEnd).toLocaleString('es-CO', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            }).replace(/\//g, '-')
                            : "-"
                        }
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-full">
                  <div className={'flex'}>
                    <div className="flex justify-between mr-2">
                      <label
                        htmlFor=""
                        className="block text-sm font-medium leading-6 text-gray-600 w-[115px]"
                      >
                        {t("share.views.assigned_by")}:
                      </label>
                    </div>
                    {isLoading ? (
                      <div className="w-full bg-gray-300 animate-pulse rounded-sm h-4" />
                    ) : (
                      <div className="truncate">
                        {nutritionProgress?.assignedBy ?? "-"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
